import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { trackAddedToCart } from "@analytics/klaviyo";
import { gaAddToCart, gaRemoveFromCart } from "@analytics/google";

import { addToCart } from "@helpers/cart/addToCart";
import { removeFromCart } from "@helpers/cart/removeFromCart";
import { refreshGifts } from "@helpers/cart/refreshGifts";

import {
  AddToCartActionPayload,
  CartState,
  RemoveFromCartActionPayload,
} from "@typings/store/cart";
import { Product } from "@typings/product/product";

const initialState: CartState = {
  cartItems: [],
  isCartOpen: false,
  isCartLoaded: false,
};

const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    getItemsFromLocalStorage: (state) => {
      state.cartItems = JSON.parse(localStorage.getItem("cart_items") || "[]");
      state.isCartLoaded = true;
    },
    hydrateSharedCart: (state, action: PayloadAction<Product[]>) => {
      state.cartItems = refreshGifts(action.payload);
      state.isCartLoaded = true;
      localStorage.setItem("cart_items", JSON.stringify(state.cartItems));
    },

    /* Modal Actions
     ============== */
    openCart: (state) => {
      state.isCartOpen = true;
    },

    closeCart: (state) => {
      state.isCartOpen = false;
    },

    toggleCart: (state) => {
      state.isCartOpen = !state.isCartOpen;
    },

    /* Cart Actions
     ============== */
    addToCart: (state, action: PayloadAction<AddToCartActionPayload>) => {
      const newCartItems = action.payload.cartItems;

      state.cartItems = addToCart(state.cartItems, newCartItems);
      state.cartItems = refreshGifts(state.cartItems);
      state.isCartLoaded = true;

      trackAddedToCart(state.cartItems, newCartItems);
      gaAddToCart(newCartItems);

      localStorage.setItem("cart_items", JSON.stringify(state.cartItems));
    },

    updateCartItem: (state, action: PayloadAction<{ oldItem: Product; newItem: Product }>) => {
      const { oldItem, newItem } = action.payload;
      const index = state.cartItems.findIndex(
        (ci) =>
          ci.item.id === oldItem.item.id &&
          JSON.stringify(ci.configuration) === JSON.stringify(oldItem.configuration)
      );

      if (index !== -1) {
        state.cartItems[index] = newItem;
        state.cartItems = refreshGifts(state.cartItems);
        localStorage.setItem("cart_items", JSON.stringify(state.cartItems));
      }
    },

    removeFromCart: (state, action: PayloadAction<RemoveFromCartActionPayload>) => {
      const removedCartItems = action.payload.cartItems;

      state.cartItems = removeFromCart(state.cartItems, removedCartItems);
      state.cartItems = refreshGifts(state.cartItems);

      if (!state.cartItems.length) state.isCartOpen = false;

      gaRemoveFromCart(removedCartItems);

      localStorage.setItem("cart_items", JSON.stringify(state.cartItems));
    },

    clearCart: (state) => {
      state.cartItems = [];

      localStorage.setItem("cart_items", JSON.stringify(state.cartItems));
    },
  },
});

export default cartSlice;
